import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { palette } from "@/constants/colors";
import { useGetParentMessagesQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

type NotificationsBellProps = {
    tone?: "default" | "onPrimary";
};

/**
 * Header bell that opens the notifications inbox. The badge is the unread count.
 */
export function NotificationsBell({
    tone = "default",
}: NotificationsBellProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const { data } = useGetParentMessagesQuery();
    const unreadCount = data?.unread_count ?? 0;
    const onPrimary = tone === "onPrimary";
    const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
    // Absolute + NativeWind min-w does not grow with text on Android; size explicitly.
    const badgeMinWidth = Math.max(16, 8 + badgeLabel.length * 7);

    return (
        <Pressable
            accessibilityRole="button"
            accessibilityLabel={
                unreadCount > 0
                    ? t("notifications.openUnread", { count: unreadCount })
                    : t("notifications.open")
            }
            className="p-2"
            style={{ overflow: "visible" }}
            hitSlop={8}
            onPress={() => router.push("/notifications")}
            testID={TEST_IDS[TEST_IDS.NOTIFICATIONS_BELL]}
        >
            <Icon
                name="bell"
                size={20}
                className={
                    onPrimary ? "text-primary-foreground" : "text-foreground"
                }
            />
            {unreadCount > 0 && (
                <View
                    pointerEvents="none"
                    style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        minWidth: badgeMinWidth,
                        height: 16,
                        paddingHorizontal: 4,
                        borderRadius: 8,
                        backgroundColor: palette.light.error,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Text className="text-white text-[10px] font-medium leading-3">
                        {badgeLabel}
                    </Text>
                </View>
            )}
        </Pressable>
    );
}
